import React, { useState, useEffect, useContext } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaChartPie,
    FaBriefcase,
    FaTasks,
    FaUser,
    FaBell,
    FaHeadset,
    FaSignOutAlt,
    FaChevronLeft,
    FaHome,
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../config';
import useAuthGuard from '../../hooks/useAuthGuard';
import './JobSeekerLayout.css';

const JobSeekerLayout = () => {
    const { user, logout } = useContext(AuthContext);
    useAuthGuard(['job_seeker', 'student']);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [seekerName, setSeekerName] = useState('Candidate');
    const [profileImage, setProfileImage] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const navigate = useNavigate();

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data) {
                    setSeekerName(res.data.name || 'Candidate');
                    setProfileImage(res.data.profileImage || '');
                }
            } catch (err) {
                console.error("Error fetching job seeker profile:", err);
            }
        };
        fetchProfile();

        const handleProfileUpdate = () => fetchProfile();
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileUpdated', handleProfileUpdate);
    }, []);

    useEffect(() => {
        const fetchUnreadCount = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const res = await axios.get('/api/notifications', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const unread = res.data.filter(n => !n.isRead).length;
                setUnreadCount(unread);
            } catch (err) {
                // silent fail
            }
        };
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000);

        const handleNotificationsRead = (e) => {
            setUnreadCount(e.detail?.count ?? 0);
        };
        window.addEventListener('notificationsRead', handleNotificationsRead);

        return () => {
            clearInterval(interval);
            window.removeEventListener('notificationsRead', handleNotificationsRead);
        };
    }, []);

    const handleLogout = () => {
        logout();
        window.dispatchEvent(new Event('authChange'));
        navigate('/auth');
    };

    const menuGroups = [
        {
            heading: 'Navigation',
            items: [
                { path: '/', name: 'Public Home', icon: <FaHome /> }
            ]
        },
        {
            heading: 'Main',
            items: [
                { path: '/jobseeker-dashboard', name: 'Hiring Hub', icon: <FaChartPie /> }
            ]
        },
        {
            heading: 'Jobs & Tracking',
            items: [
                { path: '/jobseeker-dashboard/jobs', name: 'Job Board', icon: <FaBriefcase /> },
                { path: '/jobseeker-dashboard/applications', name: 'My Applications', icon: <FaTasks /> }
            ]
        },
        {
            heading: 'Personal',
            items: [
                { path: '/jobseeker-dashboard/profile', name: 'Career Profile', icon: <FaUser /> },
                { path: '/jobseeker-dashboard/notifications', name: 'Notifications', icon: <FaBell /> },
                { path: '/jobseeker-dashboard/support', name: 'Support & FAQ', icon: <FaHeadset /> }
            ]
        }
    ];

    return (
        <div className={`js-layout ${isSidebarOpen ? '' : 'js-sidebar-closed'}`}>
            {/* Sidebar */}
            <aside className="js-sidebar">
                <button
                    className="js-sidebar-toggle"
                    onClick={toggleSidebar}
                    aria-label={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
                >
                    <FaChevronLeft />
                </button>

                <div className="js-sidebar-header">
                    <div className="js-logo-container">
                        {isSidebarOpen ? (
                            <h2 className="js-logo-text">ZenX<span className="js-accent">Staffing</span></h2>
                        ) : (
                            <h2 className="js-logo-text">Z<span className="js-accent">S</span></h2>
                        )}
                    </div>
                </div>

                <nav className="js-sidebar-nav">
                    {menuGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className="nav-group">
                            <h3 className="js-nav-heading">{group.heading}</h3>
                            <ul>
                                {group.items.map((item, index) => (
                                    <li key={index}>
                                        <NavLink
                                            to={item.path}
                                            end={item.path === '/jobseeker-dashboard'}
                                            className={({ isActive }) => (isActive ? 'js-nav-link active' : 'js-nav-link')}
                                            title={!isSidebarOpen ? item.name : ""}
                                        >
                                            <span className="js-nav-icon">{item.icon}</span>
                                            <span className="js-nav-text">{item.name}</span>
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </nav>

                <div className="js-sidebar-footer">
                    <button className="js-logout-btn" onClick={handleLogout}>
                        <span className="js-nav-icon"><FaSignOutAlt /></span>
                        <span className="js-nav-text">Sign Out</span>
                    </button>
                </div>
            </aside>

            {/* Main Content Area */}
            <div className="js-main-content-wrapper">
                <header className="js-topbar">
                    <div className="js-topbar-left">
                        <h2 className="js-topbar-greeting">Welcome back, {seekerName.split(' ')[0]}</h2>
                    </div>

                    <div className="js-topbar-right">
                        <div
                            className={`js-notification-trigger ${unreadCount > 0 ? 'js-has-unread' : ''}`}
                            onClick={() => navigate('/jobseeker-dashboard/notifications')}
                        >
                            <FaBell size={18} />
                            {unreadCount > 0 && <span className="js-unread-badge">{unreadCount}</span>}
                        </div>

                        <div className="js-profile-summary" onClick={() => navigate('/jobseeker-dashboard/profile')}>
                            <div className="js-avatar">
                                {profileImage ? (
                                    <img src={`${API_URL}${profileImage}`} alt="Profile" />
                                ) : (
                                    seekerName.charAt(0).toUpperCase()
                                )}
                            </div>
                            <div className="js-profile-text">
                                <span className="js-name">{seekerName}</span>
                                <span className="js-role">JOB SEEKER</span>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="js-main-content">
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

export default JobSeekerLayout;
